import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import {
  Shield,
  FileText,
  Cookie,
  CheckCircle2,
  ArrowLeft,
  Mail,
} from "lucide-react";

const LEGAL_TABS = [
  {
    to: "/privacy",
    label: "Privacy Policy",
    icon: Shield,
  },
  {
    to: "/terms",
    label: "Terms of Service",
    icon: FileText,
  },
  {
    to: "/cookies",
    label: "Cookie Policy",
    icon: Cookie,
  },
  {
    to: "/acceptable-use",
    label: "Acceptable Use & AI Safety",
    icon: CheckCircle2,
  },
];

export default function LegalLayout({ title, subtitle, lastUpdated = "September 10, 2026", children }) {
  const location = useLocation();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col selection:bg-accent/20">
      {/* ── Top Header ── */}
      <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center transition-opacity hover:opacity-85">
              <Logo size="sm" />
            </Link>
            <span className="hidden sm:inline-block text-xs font-bold uppercase tracking-widest text-muted-foreground border-l border-border pl-4">
              Legal &amp; Trust
            </span>
          </div>

          <div className="flex items-center gap-3">
            <ThemeToggle />
            {user ? (
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-1.5 rounded-full bg-accent px-4 py-1.5 text-xs font-bold text-white shadow-sm transition hover:bg-accent-hover"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Workspace
              </Link>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/login"
                  className="rounded-full px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
                >
                  Log in
                </Link>
                <Link
                  to="/signup"
                  className="rounded-full bg-accent px-3.5 py-1.5 text-xs font-bold text-white shadow-sm transition hover:bg-accent-hover"
                >
                  Sign up
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── Hero Banner ── */}
      <section className="border-b border-border bg-surface/50 py-10 px-4 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl text-foreground">
                {title}
              </h1>
              {subtitle && (
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                  {subtitle}
                </p>
              )}
            </div>

            <div className="shrink-0 text-left md:text-right">
              <p className="text-xs text-muted-foreground font-medium">Effective Date</p>
              <p className="text-xs font-bold text-foreground mt-0.5">{lastUpdated}</p>
            </div>
          </div>

          {/* ── Horizontal Navigation Tabs (Responsive) ── */}
          <div className="mt-8 flex gap-2 overflow-x-auto pb-1 no-scrollbar">
            {LEGAL_TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = location.pathname === tab.to;
              return (
                <Link
                  key={tab.to}
                  to={tab.to}
                  className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-xs font-bold transition-all border ${
                    isActive
                      ? "border-accent bg-accent text-white shadow-sm"
                      : "border-border bg-surface text-muted-foreground hover:border-accent/40 hover:text-foreground"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span>{tab.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Main Legal Content ── */}
      <main className="flex-1 py-10 px-4 sm:px-6">
        <div className="mx-auto max-w-4xl">
          <div className="rounded-2xl border border-border bg-surface p-6 sm:p-10 shadow-sm leading-relaxed">
            {children}
          </div>

          {/* ── Contact & Support Callout ── */}
          <div className="mt-8 rounded-2xl border border-border bg-elevated/60 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-display text-sm font-bold text-foreground">
                Questions or Data Requests?
              </h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Our legal and security team responds to all privacy inquiries, CCPA/GDPR requests, and data deletion requests within 48 hours.
              </p>
            </div>
            <a
              href="mailto:getintouch@viralityai.media"
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-4 py-2 text-xs font-bold text-foreground hover:border-accent hover:text-accent transition shadow-sm shrink-0"
            >
              <Mail className="h-3.5 w-3.5 text-accent" /> getintouch@viralityai.media
            </a>
          </div>
        </div>
      </main>

      {/* ── Footer ── */}
      <footer className="border-t border-border bg-surface/30 py-8 px-4 sm:px-6 text-xs text-muted-foreground">
        <div className="mx-auto flex max-w-6xl flex-col sm:flex-row items-center justify-between gap-4">
          <p>© {new Date().getFullYear()} Virality AI. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/privacy" className="hover:text-foreground">Privacy Policy</Link>
            <span>·</span>
            <Link to="/terms" className="hover:text-foreground">Terms of Service</Link>
            <span>·</span>
            <Link to="/cookies" className="hover:text-foreground">Cookie Policy</Link>
            <span>·</span>
            <Link to="/acceptable-use" className="hover:text-foreground">Acceptable Use</Link>
            <span>·</span>
            <Link to="/" className="hover:text-foreground">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
